/**
 * KHOJ About Us Page - Scroll Animations
 * Using Intersection Observer for performant scroll-triggered animations
 * No jQuery dependency
 */

(function() {
  'use strict';

  // Configuration
  const config = {
    threshold: 0.15,           // Trigger when 15% of element is visible
    rootMargin: '0px 0px -50px 0px', // Slight offset from bottom
    baseDelay: 0,              // Base delay in ms
    delayIncrement: 100        // Delay increment for staggered animations
  };

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Initialize scroll animations
   */
  function initAnimations() {
    if (prefersReducedMotion) {
      // If user prefers reduced motion, show all elements immediately
      document.querySelectorAll('[data-animate]').forEach(el => {
        el.classList.add('animated');
      });
      return;
    }

    // Create Intersection Observer
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: config.threshold,
      rootMargin: config.rootMargin
    });

    // Observe all elements with data-animate attribute
    document.querySelectorAll('[data-animate]').forEach(el => {
      observer.observe(el);
    });
  }

  /**
   * Handle intersection events
   */
  function handleIntersection(entries, observer) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;
        const delay = parseInt(element.dataset.delay) || 0;

        // Add animated class after delay
        setTimeout(() => {
          element.classList.add('animated');
        }, delay);

        // Stop observing once animated
        observer.unobserve(element);
      }
    });
  }

  /**
   * Parallax effect for hero section
   */
  function initParallax() {
    if (prefersReducedMotion) return;

    const hero = document.querySelector('.about-hero');
    if (!hero) return;

    const heroContent = hero.querySelector('.hero-content');
    const heroSymbol = hero.querySelector('.hero-symbol');

    let ticking = false;

    function updateParallax() {
      const scrolled = window.pageYOffset;
      const heroHeight = hero.offsetHeight;

      if (scrolled <= heroHeight) {
        const ratio = scrolled / heroHeight;
        
        // Subtle parallax on content
        if (heroContent) {
          heroContent.style.transform = `translateY(${scrolled * 0.3}px)`;
          heroContent.style.opacity = 1 - (ratio * 0.5);
        }

        // Symbol moves slightly faster
        if (heroSymbol) {
          heroSymbol.style.transform = `translateY(${scrolled * 0.4}px) scale(${1 - ratio * 0.2})`;
        }
      }

      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });
  }

  /**
   * Smooth scroll for anchor links
   */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  /**
   * Counter animation for any numeric elements
   */
  function initCounters() {
    if (prefersReducedMotion) return;

    const counters = document.querySelectorAll('[data-counter]');
    
    const counterObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const counter = entry.target;
          const target = parseInt(counter.dataset.counter);
          const duration = parseInt(counter.dataset.duration) || 2000;
          animateCounter(counter, target, duration);
          counterObserver.unobserve(counter);
        }
      });
    }, { threshold: 0.5 });

    counters.forEach(counter => counterObserver.observe(counter));
  }

  /**
   * Animate counter from 0 to target
   */
  function animateCounter(element, target, duration) {
    let start = 0;
    const startTime = performance.now();

    function update(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out quad
      const easeProgress = 1 - (1 - progress) * (1 - progress);
      
      const current = Math.floor(easeProgress * target);
      element.textContent = current;

      if (progress < 1) {
        requestAnimationFrame(update);
      } else {
        element.textContent = target;
      }
    }

    requestAnimationFrame(update);
  }

  /**
   * Text scramble effect for headlines (optional enhancement)
   */
  function initTextScramble() {
    if (prefersReducedMotion) return;

    const scrambleElements = document.querySelectorAll('[data-scramble]');
    
    scrambleElements.forEach(el => {
      const originalText = el.textContent;
      const chars = 'अआइईउऊएऐओऔकखगघचछजझञटठडढणतथदधनपफबभमयरलवशषसह';
      
      let iteration = 0;
      const interval = setInterval(() => {
        el.textContent = originalText
          .split('')
          .map((char, index) => {
            if (index < iteration) {
              return originalText[index];
            }
            if (char === ' ') return ' ';
            return chars[Math.floor(Math.random() * chars.length)];
          })
          .join('');

        if (iteration >= originalText.length) {
          clearInterval(interval);
        }

        iteration += 1/3;
      }, 30);
    });
  }

  /**
   * Reveal on scroll for line elements
   */
  function initLineReveals() {
    if (prefersReducedMotion) return;

    const lines = document.querySelectorAll('.accent-line, .hero-divider');
    
    const lineObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animated');
          lineObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    lines.forEach(line => lineObserver.observe(line));
  }

  /**
   * Section visibility tracking (for analytics if needed)
   */
  function initSectionTracking() {
    const sections = document.querySelectorAll('.about-section');
    const visibleSections = new Set();

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const sectionClass = entry.target.classList[1]; // Get section-specific class
        
        if (entry.isIntersecting) {
          visibleSections.add(sectionClass);
          // Could dispatch custom event for analytics
          // window.dispatchEvent(new CustomEvent('sectionViewed', { detail: sectionClass }));
        }
      });
    }, { threshold: 0.3 });

    sections.forEach(section => sectionObserver.observe(section));
  }

  /**
   * Scroll progress indicator (optional)
   */
  function initScrollProgress() {
    const progressBar = document.querySelector('.scroll-progress');
    if (!progressBar) return;

    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      progressBar.style.width = `${scrollPercent}%`;
    }, { passive: true });
  }

  /**
   * Initialize everything when DOM is ready
   */
  function init() {
    initAnimations();
    initParallax();
    initSmoothScroll();
    initCounters();
    initLineReveals();
    initSectionTracking();
    initScrollProgress();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use if needed
  window.KhojAbout = {
    init: init,
    initAnimations: initAnimations,
    initParallax: initParallax
  };

})();

